import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
// Intentionally avoid using ModalCloseButton here because it has slot="close",
// which will close the parent slideover/modal via React Aria context.
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { IconCheck, IconLink, IconX } from "@tabler/icons-react";
import { Duration } from "luxon";
import { type FC, useState } from "react";
import type { SnippetShareState } from "./useTranscriptSnippetSharing.js";

export const SnippetSharePopover: FC<{
  snippetToShare: SnippetShareState;
}> = (props) => {
  const { onClose, startSec, endSec, url } = props.snippetToShare;
  const [didCopy, setDidCopy] = useState(false);
  const formatTime = (seconds: number) =>
    Duration.fromMillis(Math.floor(seconds) * 1000).toFormat("m:ss");

  return (
    <div className="pointer-events-auto fixed right-0 bottom-[65px] left-0 z-10 flex justify-center">
      {/*
        Intentionally a plain element rather than React Aria's <Dialog>. The
        Dialog auto-focuses itself on mount (useDialog calls focusSafely), and in
        Firefox programmatically moving focus clears the active text selection.
        That fires `selectionchange`, the snippet-sharing hook sees a collapsed
        selection, and the popover closes immediately. Avoiding the focus steal
        keeps the popover open while the user still has text selected.

        For the same reason, onMouseDown is prevented below so clicking the
        popover's buttons doesn't move focus and clear the selection.
      */}
      <div
        role="dialog"
        aria-label="Share this snippet"
        onMouseDown={(e) => e.preventDefault()}
        className="animate-popover-enter rounded-lg border border-border-light bg-white shadow-lg"
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="text-sm font-medium text-foreground">
            Share this snippet
          </div>
          <div className="text-sm text-gray-600">
            Start: <span className="font-mono">{formatTime(startSec)}</span>
          </div>
          <div className="mr-2 text-sm text-gray-600">
            End: <span className="font-mono">{formatTime(endSec)}</span>
          </div>
          <Button
            variant={ButtonVariant.Primary}
            onClick={async () => {
              await navigator.clipboard.writeText(url);
              setDidCopy(true);
              setTimeout(() => setDidCopy(false), 2000);
            }}
            icon={
              didCopy ? (
                <IconCheck aria-hidden className="size-4 text-white" />
              ) : (
                <IconLink aria-hidden className="size-4" />
              )
            }
            className={
              didCopy
                ? "bg-success hover:bg-success focus-visible:outline-green-600"
                : undefined
            }
          >
            {didCopy ? "Link copied" : "Copy link"}
          </Button>
          <div className="flex-none">
            <IconButton
              icon={<IconX className="size-5 flex-none text-gray-400" />}
              variant={ButtonVariant.Tertiary}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
